import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { defaultAuctionsNearMeSlice } from '@/redux/modules/auctionsNearMe/auctionsNearMe.types';

import { fetchCatalogsNearMeByBounds, fetchCatalogsNearMeByCityAndState } from './auctionsNearMe.actions';

const auctionsNearMeSlice = createSlice({
    extraReducers: (builder) => {
        // Handle fetchCatalogsNearMeByBounds lifecycle
        builder.addCase(fetchCatalogsNearMeByBounds.pending, (slice, action) => {
            slice.latestRequestId = action.meta.requestId;
            slice.loading = true;
            slice.error = null;
            slice.invalidLocationPath = null;
        });
        builder.addCase(fetchCatalogsNearMeByBounds.fulfilled, (slice, action) => {
            // Ignore a response for a request that a later dispatch has since superseded (e.g. two
            // bounds fetches racing after a rapid map pan) — applying it would overwrite a fresher
            // result, or clear `loading` while the newer request is still in flight (SEAR-1816).
            if (action.meta.requestId !== slice.latestRequestId) {
                return;
            }
            const { payload } = action;
            slice.loading = false;
            slice.error = null;
            if (!payload.error && payload.payload) {
                // The backend omits catalogIds/catalogsNearMe/otherPlaces entirely (not empty
                // arrays) for a page past the end of the result set — e.g. ?page=99 on a location
                // with only 1 page. Default them so a real, in-range ?page= from a bookmark, a
                // crawler, or a stale link degrades to an empty page instead of a TypeError on
                // otherPlaces.length crashing the SSR render.
                const {
                    catalogIds = [],
                    catalogsNearMe = [],
                    currentPage = 0,
                    mapPoints = [],
                    otherPlaces = [],
                    totalPages = 0,
                    totalRecords = 0,
                    totalUniqueHouses = 0,
                } = payload.payload;
                slice.catalogIds = catalogIds;
                slice.catalogsNearMe = catalogsNearMe;
                slice.mapPoints = mapPoints;
                // Preserve existing otherPlaces when the new response has fewer
                // (e.g. pagination or filter requests may return a subset)
                if (otherPlaces.length >= slice.otherPlaces.length) {
                    slice.otherPlaces = otherPlaces;
                }
                slice.currentPage = currentPage;
                slice.totalPages = totalPages;
                slice.totalRecords = totalRecords;
                slice.totalUniqueHouses = totalUniqueHouses;
            }
        });
        builder.addCase(fetchCatalogsNearMeByBounds.rejected, (slice, action) => {
            if (action.meta.requestId !== slice.latestRequestId) {
                return;
            }
            slice.loading = false;
            slice.error = action.error.message || 'Failed to fetch catalogs by bounds';
        });

        // Handle fetchCatalogsNearMeByCityAndState lifecycle
        builder.addCase(fetchCatalogsNearMeByCityAndState.pending, (slice, action) => {
            slice.latestRequestId = action.meta.requestId;
            slice.loading = true;
            slice.error = null;
            slice.invalidLocationPath = null;
        });
        builder.addCase(fetchCatalogsNearMeByCityAndState.fulfilled, (slice, action) => {
            if (action.meta.requestId !== slice.latestRequestId) {
                return;
            }
            const { payload } = action;
            slice.loading = false;
            slice.error = null;
            if (!payload.error && payload.payload) {
                // See the matching comment on fetchCatalogsNearMeByBounds.fulfilled — the backend
                // omits these fields entirely for a page past the end of the result set.
                const {
                    catalogIds = [],
                    catalogsNearMe = [],
                    currentPage = 0,
                    mapPoints = [],
                    otherPlaces = [],
                    totalPages = 0,
                    totalRecords = 0,
                    totalUniqueHouses = 0,
                } = payload.payload;
                slice.catalogIds = catalogIds;
                slice.catalogsNearMe = catalogsNearMe;
                slice.mapPoints = mapPoints;
                slice.otherPlaces = otherPlaces;
                slice.currentPage = currentPage;
                slice.totalPages = totalPages;
                slice.totalRecords = totalRecords;
                slice.totalUniqueHouses = totalUniqueHouses;
            }
        });
        builder.addCase(fetchCatalogsNearMeByCityAndState.rejected, (slice, action) => {
            if (action.meta.requestId !== slice.latestRequestId) {
                return;
            }
            slice.loading = false;
            slice.error = action.error.message || 'Failed to fetch catalogs by city/state';
            // A rejection here still marks the location invalid — catalog-management answers an
            // unknown city slug with a flat 500, indistinguishable from a genuine outage, and a
            // 404 beats rendering "no auctions" for a city that does not exist. It happens in
            // `auctionsNearMePageData`, the only dispatcher of this thunk, because the path the
            // flag has to be recorded against lives there and not in the request.
        });
    },
    initialState: defaultAuctionsNearMeSlice,
    name: 'auctionsNearMe',
    reducers: {
        clearCatalogsNearMe: (slice) => {
            slice.catalogIds = [];
            slice.catalogsNearMe = [];
            slice.mapPoints = [];
            slice.otherPlaces = [];
            slice.currentPage = 0;
            slice.totalPages = 0;
            slice.totalRecords = 0;
            slice.totalUniqueHouses = 0;
            slice.loading = false;
            slice.error = null;
            slice.invalidLocationPath = null;
            slice.latestRequestId = null;
        },
        /**
         * Flags the pathname as an unresolvable location — an unrecognized single URL segment, a
         * malformed state/city pair, or a city the backend could not resolve. Takes the path so
         * the flag dies with the URL that earned it instead of leaking into the next page.
         */
        markLocationInvalid: (slice, action: PayloadAction<string>) => {
            slice.invalidLocationPath = action.payload;
        },
    },
});

export const { reducer: auctionsNearMeReducer } = auctionsNearMeSlice;

export const { clearCatalogsNearMe, markLocationInvalid } = auctionsNearMeSlice.actions;
